import * as THREE from 'three';
import { UNSLEEPING } from '@somnhold/content';
import type { Enemy } from '@somnhold/sim';
import { WORLD_PER_HEX } from '../coords.ts';
import type { UnRender } from './ctx.ts';

/*
 * An Unsleeping's state of his fight on the ground, each frame, in his colours:
 * - His ultimate, channelled: its shape marked on the ground — a cone from him (Corvin) or circles where it will fall
 *   — a dim field the size of the blow, a brighter fill sweeping out as the channel runs (when it is full, it lands)
 *   and an edge that beats faster toward the end. Get out of it, or cut it.
 * - Exposed (his channel was cut): a pale crack of light pulsing at his feet while he takes more.
 * - His rage, high: a ring at his feet that tightens and brightens as it fills.
 * - What he set loose (embers): a glowing ball each.
 * One group, a handful of meshes made once; only one Unsleeping is out at a time.
 */

const K = WORLD_PER_HEX / 1000;
const TAU = Math.PI * 2;
const SPOTS = 6;
const ORBS = 4;

// Plain (not additive) blending: on the pale flagstones an additive mark washes out to white.
const flat = (geo: THREE.BufferGeometry, opacity: number): THREE.Mesh => {
  const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide }));
  m.rotation.x = Math.PI / 2;
  m.renderOrder = 3;
  m.frustumCulled = false;
  return m;
};
const mat = (m: THREE.Mesh): THREE.MeshBasicMaterial => m.material as THREE.MeshBasicMaterial;

export class UnsleepingFx {
  readonly group = new THREE.Group();
  /** Things placed in the world (spots, orbs), not at his feet. */
  readonly world = new THREE.Group();
  /** The cone, turned on its own pivot to where he aims (unit radius, scaled to his). */
  private readonly pivot = new THREE.Group();
  private readonly field: THREE.Mesh;
  private readonly fill: THREE.Mesh;
  private readonly edge: THREE.Mesh;
  private readonly crack: THREE.Mesh;
  private readonly rage: THREE.Mesh;
  /** Circles where it will fall: field, fill and edge each. */
  private readonly spots: { g: THREE.Group; field: THREE.Mesh; fill: THREE.Mesh; edge: THREE.Mesh }[] = [];
  private readonly orbs: THREE.Mesh[] = [];
  private half = -1;

  constructor() {
    this.field = flat(new THREE.CircleGeometry(1, 40, 0, 1), 0.16);
    this.fill = flat(new THREE.CircleGeometry(1, 40, 0, 1), 0.32);
    this.edge = flat(new THREE.RingGeometry(0.965, 1, 40, 1, 0, 1), 0.7);
    this.pivot.add(this.field, this.fill, this.edge);
    this.crack = flat(new THREE.RingGeometry(0.55, 0.9, 24), 0.6);
    this.rage = flat(new THREE.RingGeometry(0.7, 0.82, 32), 0.5);
    this.group.add(this.pivot, this.crack, this.rage);
    const disc = new THREE.CircleGeometry(1, 36);
    const rim = new THREE.RingGeometry(0.96, 1, 36);
    for (let i = 0; i < SPOTS; i++) {
      const g = new THREE.Group();
      const s = { g, field: flat(disc, 0.16), fill: flat(disc, 0.3), edge: flat(rim, 0.7) };
      g.add(s.field, s.fill, s.edge);
      this.spots.push(s);
      this.world.add(g);
    }
    const ball = new THREE.SphereGeometry(1, 12, 8);
    for (let i = 0; i < ORBS; i++) {
      const o = new THREE.Mesh(ball, new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending }));
      o.frustumCulled = false;
      this.orbs.push(o);
      this.world.add(o);
    }
    this.group.visible = this.world.visible = false;
  }

  /** The cone's shape (rebuilt only when another Unsleeping's differs). */
  private shape(half: number): void {
    if (half === this.half) return;
    this.half = half;
    const a = (half / 1024) * TAU;
    for (const m of [this.field, this.fill]) {
      m.geometry.dispose();
      m.geometry = new THREE.CircleGeometry(1, 40, -a, a * 2);
    }
    this.edge.geometry.dispose();
    this.edge.geometry = new THREE.RingGeometry(0.965, 1, 40, 1, -a, a * 2);
  }

  /** One frame of his fight, at his feet `p` (world); `ground(x, y)` lifts a sim point to the board. */
  frame(e: Enemy, p: THREE.Vector3, now: number, r: UnRender, ground: (x: number, y: number) => THREE.Vector3): void {
    const u = e.un;
    this.group.visible = this.world.visible = !!u;
    if (!u) return;
    const look = r.look;
    this.group.position.set(p.x, p.y + 0.04, p.z);
    this.group.scale.setScalar(1);
    this.world.position.set(0, 0, 0);
    this.world.scale.setScalar(1);
    const casting = u.cast > 0 && u.castLen > 0;
    const k = casting ? 1 - u.cast / u.castLen : 0;
    const beat = 0.5 + 0.5 * Math.sin(now * (6 + 18 * k));
    // A cone from him.
    this.pivot.visible = casting && !!r.cone && u.spots.length === 0;
    if (this.pivot.visible && r.cone) {
      this.shape(r.cone.half);
      const R = r.cone.radius * K;
      this.pivot.rotation.y = -(u.castA / 1024) * TAU;
      this.field.scale.set(R, R, 1);
      this.edge.scale.set(R, R, 1);
      const f = R * Math.max(0.04, k);
      this.fill.scale.set(f, f, 1);
      mat(this.field).color.set(look.glow);
      mat(this.fill).color.set(look.accent);
      mat(this.edge).color.set(look.accent);
      mat(this.edge).opacity = 0.55 + 0.4 * beat;
      mat(this.field).opacity = 0.16 + 0.12 * k;
    }
    // Circles where it will fall.
    for (let i = 0; i < SPOTS; i++) {
      const s = this.spots[i] as (typeof this.spots)[number];
      const at = casting ? u.spots[i] : undefined;
      s.g.visible = !!at;
      if (!at) continue;
      const q = ground(at.x, at.y);
      s.g.position.set(q.x, q.y + 0.05, q.z);
      const R = at.r * K;
      s.field.scale.set(R, R, 1);
      s.edge.scale.set(R, R, 1);
      const f = R * Math.max(0.04, k);
      s.fill.scale.set(f, f, 1);
      mat(s.field).color.set(look.glow);
      mat(s.fill).color.set(look.accent);
      mat(s.edge).color.set(look.accent);
      mat(s.edge).opacity = 0.55 + 0.4 * beat;
      mat(s.field).opacity = 0.16 + 0.12 * k;
    }
    // Cut and exposed: a pale crack pulsing at his feet.
    this.crack.visible = u.exposed > 0;
    if (this.crack.visible) {
      const b = 0.5 + 0.5 * Math.sin(now * 14);
      this.crack.scale.setScalar(1 + 0.15 * b);
      mat(this.crack).color.set(look.pale);
      mat(this.crack).opacity = 0.35 + 0.45 * b;
    }
    // Rage past half: a ring at his feet, tightening and brightening as it fills.
    const rk = u.rage / UNSLEEPING.rageMax;
    this.rage.visible = !casting && rk > 0.5;
    if (this.rage.visible) {
      const q = (rk - 0.5) * 2;
      this.rage.scale.setScalar(1.6 - 0.6 * q);
      mat(this.rage).color.set(look.accent);
      mat(this.rage).opacity = 0.2 + 0.6 * q * (0.7 + 0.3 * Math.sin(now * (4 + 10 * q)));
    }
    // What he set loose.
    for (let i = 0; i < ORBS; i++) {
      const o = this.orbs[i] as THREE.Mesh;
      const at = u.orbs[i];
      o.visible = !!at;
      if (!at) continue;
      const q = ground(at.x, at.y);
      o.position.set(q.x, q.y + 0.7 + 0.08 * Math.sin(now * 9 + i), q.z);
      o.scale.setScalar(0.22 + 0.04 * Math.sin(now * 17 + i));
      (o.material as THREE.MeshBasicMaterial).color.set(look.accent);
    }
  }

  /** Every mark drawn far under the ground: out of sight, but its shaders compile with the scene's. */
  park(): void {
    this.group.visible = this.world.visible = true;
    this.pivot.visible = this.crack.visible = this.rage.visible = true;
    for (const s of this.spots) s.g.visible = true;
    for (const o of this.orbs) o.visible = true;
    for (const g of [this.group, this.world]) {
      g.position.set(0, -60, 0);
      g.scale.setScalar(0.0001);
    }
  }

  hide(): void {
    this.group.visible = this.world.visible = false;
  }

  dispose(): void {
    for (const g of [this.group, this.world])
      g.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          (o.material as THREE.Material).dispose();
        }
      });
  }
}
